import React, { useState, useEffect, useRef } from 'react';
import { MdFileDownload, MdOutlineFilePresent, MdLogout } from 'react-icons/md';
import { useFormContext } from '../context/FormContext';
import { useReadOnly } from '../hooks/useReadOnly';
import { Spinner } from 'reactstrap';
import Session from './auth/Session';
import { formatDate, formatCurrency } from './util/formatters';
import { generateNmDocument } from './services/DocumentService';

const OWNER_TYPE_LABELS = {
    INDIVIDUAL: 'Individual',
    SOLE_PROPRIETORSHIP: 'Sole Proprietorship',
    LIMITED_LIABILITY_COMPANY: 'LLC',
    PARTNERSHIP: 'Partnership',
    CORPORATION: 'Corporation',
    S_CORPORATION: 'S-Corporation',
    TRUST: 'Trust',
};

const TRIGGER_DISPLAY_NAMES = {
    single: 'Proxy Wind',
    dual: 'Proxy Wind + Anemometer',
    triple: 'Proxy Wind + Anemometer + Storm Track',
};

// Document type display labels
const DOCUMENT_TYPE_LABELS = {
    QUOTE: 'Quote.pdf',
    POLICY: 'Policy.pdf',
    APPLICATION: 'Application.pdf',
    DES: 'Diligent Effort Statement.pdf',
    INVOICE: 'Invoice.pdf',
};

const POLL_INTERVAL_MS = 20000;  // Poll our API every 20 seconds
const RECENT_WINDOW_MS = 300000; // 5 minutes - within this window we poll, after we call NM directly

function buildAddress(line1, line2, city, state, zip) {
    return [
        line1,
        line2,
        city && state ? `${city}, ${state} ${zip || ''}`.trim() : '',
    ].filter(Boolean).join('\n');
}

function getMissingDocs(quote, existingDocs) {
    const existingTypes = new Set(existingDocs.map(d => d.documentType));
    const missing = [];
    if (quote.quoteNumber && !existingTypes.has('QUOTE'))
        missing.push('QUOTE');
    if (quote.policyNumber && !existingTypes.has('POLICY'))
        missing.push('POLICY');
    if (quote.policyNumber && !existingTypes.has('INVOICE'))
        missing.push('INVOICE');
    return missing;
}

function isWithinRecentWindow(updatedOn) {
    if (!updatedOn) return false;
    const ageMs = Date.now() - new Date(updatedOn).getTime();
    return ageMs < RECENT_WINDOW_MS;
}

export function UserDashboard({ history }) {
    const { formData, resetForm } = useFormContext();
    const { isReadOnly } = useReadOnly();

    
    // Local state for dashboard data
    // For regular users - populated from GetDashboardDataByEmail
    // For Admin/CSR - quote data comes from FormContext, documents from GetDashboardDataById
    const [dashData, setDashData] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloadingId, setDownloadingId] = useState(null);
    const [fetchingMissing, setFetchingMissing] = useState(false);
    const [missingFetchFailed, setMissingFetchFailed] = useState(false);

    const roles = Session.getRole() || '';
    const isAdmin = roles.includes('Admin');
    const isCSR = roles.includes('CSR');
    const isAdminOrCSR = isAdmin || isCSR;

    // Ref to hold the polling interval so we can clear it on unmount
    const pollIntervalRef = useRef(null);

    useEffect(() => {
        loadDashboardData();

        // Clear polling interval on unmount
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    // Fetch documents from our API
    const fetchDocumentsFromApi = async () => {
        const token = Session.getToken();
        const email = Session.getUserName();
        if (!email) return null;

        const response = await fetch(
            `api/Quote/GetDashboardDataByEmail?email=${encodeURIComponent(email)}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!response.ok) return null;

        const result = await response.json();
        if (!result.success || !result.data) return null;

        return result.data;
    };

    // Load dashboard data
    const loadDashboardData = async () => {
        setLoading(true);
        setError('');
        try {
            const token = Session.getToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            if (isReadOnly && formData.QuoteId) {
                // Admin/CSR path
                const response = await fetch(
                    `api/Quote/GetDashboardDataById/${formData.QuoteId}`,
                    { headers }
                );
                if (response.ok) {
                    const result = await response.json();
                    if (result.success) {
                        const loadedDocs = result.data.documents || [];
                        setDocuments(loadedDocs);
                        handleMissingDocuments(result.data.quote, loadedDocs);
                    }
                }
                setLoading(false);
                return;
            }

            // Regular user path
            const data = await fetchDocumentsFromApi();
            if (!data) {
                setError('Failed to load your policy data. Please try again.');
                setLoading(false);
                return;
            }

            const quote = data.quote;
            const loadedDocs = data.documents || [];

            setDashData(quote);
            setDocuments(loadedDocs);
            setLoading(false);

            // Check for missing documents and start the appropriate strategy
            if (!isAdminOrCSR) {
                handleMissingDocuments(quote, loadedDocs);
            }

        } catch (err) {
            console.error('Error loading dashboard data:', err);
            setError('Something went wrong. Please try again.');
            setLoading(false);
        }
    };

    // Missing document strategy
    const handleMissingDocuments = (quote, existingDocs) => {
        const missing = getMissingDocs(quote, existingDocs);
        if (missing.length === 0) return;

        setFetchingMissing(true);
        setMissingFetchFailed(false);

        if (isWithinRecentWindow(quote.updatedOn)) {
            // Within 5 minute window - poll our API every 20 seconds
            // The ProcessingPayment background call is likely still running
            startPolling(quote, missing);
        } else {
            // Outside 5 minute window - call NormanMax directly for missing docs
            callNormanMaxForMissing(quote, missing);
        }
    };

    // Poll our own API every 20 seconds
    // Used within the 5 minute window after policy issuance.
    // Stops when: all docs appear, OR 5 minute window expires (then calls NM directly).
    const startPolling = (quote, initialMissing) => {
        let missing = [...initialMissing];

        pollIntervalRef.current = setInterval(async () => {
            try {
                const data = await fetchDocumentsFromApi();
                if (!data) return;

                const currentMissing = getMissingDocs(data.quote, data.documents);

                // Update documents in state on every poll
                setDashData(data.quote);
                setDocuments(data.documents || []);

                if (currentMissing.length === 0) {
                    // All documents have appeared - stop polling
                    clearInterval(pollIntervalRef.current);
                    setFetchingMissing(false);
                    return;
                }

                missing = currentMissing;

                // Check if we've crossed the 5 minute window
                if (!isWithinRecentWindow(data.quote.updatedOn)) {
                    // Window expired - stop polling and call NormanMax directly
                    clearInterval(pollIntervalRef.current);
                    callNormanMaxForMissing(data.quote, missing);
                }

            } catch (err) {
                console.error('Polling error:', err);
            }
        }, POLL_INTERVAL_MS);
    };

    // Call NormanMax directly for missing documents
    // Used when outside the 5 minute window - ProcessingPayment call has likely failed or never ran.
    // No delay needed since enough time has passed.
    const callNormanMaxForMissing = async (quote, missing) => {
        let anySucceeded = false;

        for (const docType of missing) {
            try {
                // No delay (0) - enough time has passed since binding
                await generateNmDocument(quote.id, docType, 0);
                anySucceeded = true;
            } catch (err) {
                console.error(`Failed to fetch ${docType} from NormanMax:`, err);
            }
        }

        setFetchingMissing(false);

        if (anySucceeded) {
            try {
                if (isReadOnly && quote.id) {
                    // Admin/CSR path - reload by quoteId to avoid loading wrong user's data
                    const token = Session.getToken();
                    const response = await fetch(
                        `api/Quote/GetDashboardDataById/${quote.id}`,
                        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                    );
                    if (response.ok) {
                        const result = await response.json();
                        if (result.success) {
                            setDocuments(result.data.documents || []);
                        }
                    }
                } else {
                    // Regular user path
                    const data = await fetchDocumentsFromApi();
                    if (data) {
                        setDashData(data.quote);
                        setDocuments(data.documents || []);
                    }
                }
            } catch (err) {
                console.error('Error reloading after NM fetch:', err);
            }
        } else {
            setMissingFetchFailed(true);
        }
    };

    // Download a document
    const handleDownloadDocument = async (doc) => {
        setDownloadingId(doc.id);
        try {
            const token = Session.getToken();
            const response = await fetch(`api/Document/GetDocument/${doc.id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
                console.error('Could not load document.');
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error('Could not download document:', err);
        } finally {
            setDownloadingId(null);
        }
    };

    // Determine which quote data to display
    const q = isAdminOrCSR ? {
        firstName: formData.FirstName,
        lastName: formData.LastName,
        ownerType: formData.OwnerType,
        userName: formData.UserName,
        phoneNumberPrimary: formData.PhoneNumberPrimary,
        targetAddressOne: formData.TargetAddressOne,
        targetAddressTwo: formData.TargetAddressTwo,
        targetCity: formData.TargetCity,
        targetState: formData.TargetState,
        targetZipCode: formData.TargetZipCode,
        contactAddressOne: formData.ContactAddressOne,
        contactAddressTwo: formData.ContactAddressTwo,
        contactCity: formData.ContactCity,
        contactState: formData.ContactState,
        contactZipCode: formData.ContactZipCode,
        coverageAmount: formData.CoverageAmount,
        selectedTriggerType: formData.SelectedTriggerType,
        selectedTotalPremium: formData.SelectedTotalPremium,
        effectiveDate: formData.EffectiveDate,
        policyNumber: formData.PolicyNumber,
        policyEffectiveDate: formData.EffectiveDate,
        policyExpiryDate: formData.ExpiryDate,
        policyTotalPremium: formData.PolicyTotalPremium,
        hasAdditionalParties: formData.HasAdditionalParties,
        additionalPartyName: [formData.AdditionalPartyFirstName, formData.AdditionalPartyLastName]
            .filter(Boolean).join(' '),
        status: formData.Status || formData.status || '',
    } : dashData;

    // DES and INVOICE only visible to Admin
    const visibleDocuments = documents.filter(doc =>
        doc.documentType !== 'DES' && doc.documentType !== 'INVOICE' || isAdmin
    );

    const handleLogout = () => {
        Session.removeUserSession();
        resetForm();
        window.location.href = '/login';
    };

    const insuredName = q
        ? [q.firstName, q.lastName].filter(Boolean).join(' ') || '-'
        : '-';

    const ownerTypeLabel = q
        ? OWNER_TYPE_LABELS[q.ownerType] || q.ownerType || '-'
        : '-';

    const triggerLabel = q
        ? TRIGGER_DISPLAY_NAMES[q.selectedTriggerType] || '-'
        : '-';

    const propertyAddress = q
        ? buildAddress(q.targetAddressOne, q.targetAddressTwo, q.targetCity, q.targetState, q.targetZipCode)
        : '';

    const mailingAddress = q
        ? buildAddress(q.contactAddressOne, q.contactAddressTwo, q.contactCity, q.contactState, q.contactZipCode)
        : '';

    const totalPremium = q
        ? q.policyTotalPremium || q.selectedTotalPremium
        : null;

    const additionalPartyName = q?.hasAdditionalParties
        ? q.additionalPartyName || '-'
        : null;

    return (
        <div className="dashboard-shell">
            <header className="dashboard-header">
                {isAdmin && (q?.status || '').trim().toLowerCase() === 'referred' && (
                    <button className="dashboard__logout" onClick={() => history.push('/admin/referred-policy')} style={{ marginRight: 'var(--space-md)' }}>
                        Edit policy
                    </button>
                )}
                <button className="dashboard__logout" onClick={handleLogout}>
                    Log out
                    <MdLogout size={16} style={{ marginLeft: '4px' }} />
                </button>
            </header>

            <main className="signup-card dashboard-card">
                <div className="signup-content">

                    {loading && (
                        <div className="dp-loading" aria-label="Loading policy…">
                            <Spinner style={{color: `var(--brand-primary)`}}/>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="dp-error">
                            <p className="dp-error__message">{error}</p>
                            <button
                                className="btn signup-cta btn--inverse-cta"
                                onClick={loadDashboardData}
                                style={{ marginTop: 'var(--space-md)' }}
                            >
                                Try again
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {/* Policy summary */}
                            <h1 className="text-h1" style={{ marginTop: 'var(--space-sm)', marginBottom: `var(--space-lg)` }}>
                                Policy summary
                            </h1>

                            {/* Policy number + term */}
                            <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                                <div className="summary-row" style={{ marginBottom: 0 }}>
                                    <span className="text-xs">Policy number</span>
                                    <span className="text-default--primary">
                                        {q?.policyNumber || '-'}
                                    </span>
                                </div>
                                <div className="summary-row" style={{ marginBottom: 0 }}>
                                    <span className="text-xs">Policy term</span>
                                    <span className="text-default--primary">Annual</span>
                                </div>
                            </div>

                            {/* Effective + expiration */}
                            <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                                <div className="summary-row" style={{ marginBottom: 0 }}>
                                    <span className="text-xs">Effective date</span>
                                    <span className="text-default--primary">
                                        {( (q?.status || '').trim().toLowerCase() === 'referred' ) ? '-' : formatDate(q?.policyEffectiveDate)}
                                    </span>
                                </div>
                                <div className="summary-row" style={{ marginBottom: 0 }}>
                                    <span className="text-xs">Expiration date</span>
                                    <span className="text-default--primary">
                                        {formatDate(q?.policyExpiryDate)}
                                    </span>
                                </div>
                            </div>

                            {/* Property address */}
                            <div className="summary-row">
                                <span className="text-xs">Property address</span>
                                <span className="text-default--primary" style={{ whiteSpace: 'pre-line' }}>
                                    {propertyAddress || '-'}
                                </span>
                            </div>

                            {/* Coverage + data point */}
                            <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                                <div className="summary-row" style={{ marginBottom: 0 }}>
                                    <span className="text-xs">Total coverage amount</span>
                                    <span className="text-default--primary">
                                        {formatCurrency(q?.coverageAmount)}
                                    </span>
                                </div>
                                <div className="summary-row" style={{ marginBottom: 0 }}>
                                    <span className="text-xs">Data point option</span>
                                    <span className="text-default--primary">{triggerLabel}</span>
                                </div>
                            </div>

                            {/* Total annual premium */}
                            <div className="summary-row" style={{ marginBottom: 'var(--space-xl)' }}>
                                <span className="text-xs">Total annual premium</span>
                                <span className="text-default--primary" style={{
                                    fontSize: 'var(--fs-h4)',
                                    fontWeight: 'var(--weight-semibold)'
                                }}>
                                    {/* {formatCurrency(totalPremium)} */}
                                    {formatCurrency(q?.selectedTotalPremium)}
                                </span>
                            </div>

                            {/* Policyholder details */}
                            <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                                <p className="text-h4--brand" style={{ marginBottom: 'var(--space-lg)' }}>Policyholder details</p>

                                <div className="summary-row">
                                    <span className="text-xs">Insured name</span>
                                    <span className="text-default--primary">{insuredName}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="text-xs">Property owner type</span>
                                    <span className="text-default--primary">{ownerTypeLabel}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="text-xs">Mailing address</span>
                                    <span className="text-default--primary" style={{ whiteSpace: 'pre-line' }}>
                                        {mailingAddress || '-'}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span className="text-xs">Email address</span>
                                    <span className="text-default--primary">
                                        {q?.userName || q?.email || '-'}
                                    </span>
                                </div>
                                <div className="summary-row"
                                    style={{ marginBottom: additionalPartyName ? undefined : 0 }}>
                                    <span className="text-xs">Phone number</span>
                                    <span className="text-default--primary">
                                        {q?.phoneNumberPrimary || '-'}
                                    </span>
                                </div>

                                {additionalPartyName && (
                                    <div className="summary-row" style={{ marginBottom: 0 }}>
                                        <span className="text-xs">Additional party</span>
                                        <span className="text-default--primary">{additionalPartyName}</span>
                                    </div>
                                )}
                            </div>

                            {/* Policy documents */}
                            <div className="summary-card">
                                <p className="text-h4--brand" style={{ marginBottom: 'var(--space-lg)' }}>Policy documents</p>
                                <div className="dashboard__docs">

                                    {/* No documents and nothing in progress */}
                                    {visibleDocuments.length === 0 && !fetchingMissing && !missingFetchFailed && (
                                        <p className="signup-section__desc" style={{ margin: 0 }}>
                                            No documents available.
                                        </p>
                                    )}

                                    {/* Existing documents */}
                                    {visibleDocuments.map((doc) => (
                                        <button
                                            key={doc.id}
                                            className="dashboard__doc-row"
                                            onClick={() => handleDownloadDocument(doc)}
                                            disabled={downloadingId === doc.id}
                                        >
                                            <MdOutlineFilePresent size={18}
                                                className="dashboard__doc-icon" />
                                            <span>
                                                {downloadingId === doc.id
                                                    ? 'Loading…'
                                                    : DOCUMENT_TYPE_LABELS[doc.documentType]
                                                        || doc.fileName}
                                            </span>
                                            <MdFileDownload size={18}
                                                className="dashboard__doc-download" />
                                        </button>
                                    ))}

                                    {/* Spinner while polling or fetching from NM */}
                                    {fetchingMissing && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-sm)',
                                            padding: 'var(--space-sm) 0',
                                            fontSize: 'var(--fs-small)',
                                            color: 'var(--text-primary)',
                                            opacity: 0.6,
                                        }}>
                                            <Spinner size="sm" />
                                            <span>Fetching remaining documents…</span>
                                        </div>
                                    )}

                                    {/* Failed state */}
                                    {missingFetchFailed && !fetchingMissing && (
                                        <p style={{
                                            margin: visibleDocuments.length > 0
                                                ? 'var(--space-sm) 0 0'
                                                : 0,
                                            fontSize: 'var(--fs-small)',
                                            color: 'var(--text-primary)',
                                            opacity: 0.6,
                                            lineHeight: 'var(--lh-normal)',
                                        }}>
                                            Some documents are still being prepared.
                                            Please refresh the page or check back shortly.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>

            <footer className="signup-footer" style={{ marginTop: 'var(--space-lg)' }}>
                <p>Need assistance?</p>
                <p>Call us at <a href="tel:8772695422">877-269-5422</a> to connect.</p>
            </footer>
        </div>
    );
}